import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { defaults, filter, map } from 'lodash';
import { DynamicModule } from 'ng-dynamic-component';

import {
  ContextMenuModule,
  PreventBubbleDirective,
} from '@lockton-apps/shared/ui/lui';

import {
  DynamicTableContext,
  dynamicTableContextGuards,
} from '../../interfaces/dynamic-table.interface';
import { DynamicTableComponent } from '../table/dynamic-table.component';
import { DynamicTableColumnTemplate } from '../templates/column-template.directive';

interface ContextMenuProps {
  actions: DynamicTableContext[];
}

@Component({
    selector: 'la-dynamic-table-context-menu',
    imports: [
        CommonModule,
        ContextMenuModule,
        MatIconModule,
        MatTooltipModule,
        OverlayModule,
        MatMenuModule,
        PreventBubbleDirective,
        RouterLink,
        MatMenuModule,
        DynamicModule,
    ],
    templateUrl: './dynamic-table-context-menu.component.html',
    styleUrls: ['./dynamic-table-context-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicTableContextMenuComponent
  extends DynamicTableColumnTemplate<ContextMenuProps>
  implements OnChanges
{
  @Input() isOpen!: boolean;
  @Output() contextChanged = new EventEmitter<string | number | undefined>();
  @Output() childOutput = new EventEmitter<{ id: string; event: unknown }>();

  inlineComponentId!: string | undefined;
  hasVisibleContextItems!: boolean;

  inlineActions: DynamicTableContext[] = [];
  allActions: DynamicTableContext[] = [];

  guards = dynamicTableContextGuards;

  constructor(private table: DynamicTableComponent<unknown>) {
    super();
  }

  ngOnChanges(): void {
    this.hasVisibleContextItems =
      filter(this.props.actions, (action: DynamicTableContext) => {
        return !action.exposed;
      }).length > 0;

    const processedActions = this.processActions();

    const filteredActions = filter(processedActions, (action) => {
      if (action.hidden) {
        return !action.hidden(this.entity);
      }

      return true;
    });

    this.inlineActions = filter(filteredActions, { exposed: true });
    this.allActions = filteredActions;
  }

  setInlineComponent(
    inlineComponentId?: string,
    rowId?: string | number
  ): void {
    this.inlineComponentId = inlineComponentId;
    this.contextChanged.emit(rowId);
  }

  private processActions(): DynamicTableContext[] {
    return map(this.props.actions, (action) => {
      switch (action.type) {
        case 'link':
          return {
            ...action,
            link: action.linkCreator?.(this.entity),
          };
        case 'inlineComponent':
          return {
            ...action,
            inlineComponent: {
              ...action.inlineComponent,
              inputs: action.inlineComponent.inputsCreator
                ? action.inlineComponent.inputsCreator({
                    entity: this.entity,
                    additionalData: this.additionalData,
                  })
                : {},
              outputs: {
                event: (event: any) => {
                  this.table.customEvent.emit(
                    defaults(
                      { id: `${action.id}.${event.id}`, entity: this.entity },
                      event
                    )
                  );
                },
              },
            },
          };
        default:
          return action;
      }
    });
  }
}
